import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bot, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { useChat } from "@/components/chat/useChat";
import { forgetAssistantConversation } from "@/lib/lastAssistantConversation";

/**
 * Entry params, written by the home dashboard: `q` opens the assistant already
 * asking the question the user typed on the dashboard, `voice` opens it with
 * the mic live. Both exist so the shortest path from an intent to the assistant
 * is one interaction, not "navigate, then repeat yourself".
 */
interface AssistantIndexSearch {
	q?: string;
	voice?: boolean;
}

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/assistant/")({
	validateSearch: (search: Record<string, unknown>): AssistantIndexSearch => ({
		q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
		voice: search.voice === true || search.voice === "true" ? true : undefined,
	}),
	component: AssistantIndexPage,
});

function AssistantIndexPage() {
	const { orgId } = Route.useParams();
	const { q: initialQuestion, voice: startVoice } = Route.useSearch();
	const navigate = useNavigate();
	const { t } = useTranslation();

	// The blank assistant is a new conversation: stop menu links from reopening the old one.
	useEffect(() => {
		forgetAssistantConversation(orgId);
	}, [orgId]);

	// Farm scope selector state — undefined = all farms
	const [selectedFarmId, setSelectedFarmId] = useState<string | undefined>(
		undefined,
	);

	// Fetch available farms for the scope selector
	const { data: farmsData } = useQuery(
		listEstablishmentsQueryOptions(orgId, { page: 1, limit: 50 }),
	);
	const farms =
		(farmsData as { data?: { id: string; name: string }[] } | undefined)
			?.data ?? [];

	const suggestedPrompts = t("grass.assistant.suggestedPrompts", {
		returnObjects: true,
		defaultValue: [
			"How many animals do I have across all my farms?",
			"What is the status of my paddocks across all farms?",
			"Which farms do I have registered?",
			"What are the best practices for cattle management?",
		],
	}) as string[];

	const {
		messages,
		activeAgent,
		sendMessage,
		retryLast,
		isStreaming,
		conversationId,
		error,
	} = useChat({
		orgId,
		establishmentId: selectedFarmId,
		onConversationCreated: (id) => {
			navigate({
				to: "/app/org/$orgId/assistant/$conversationId",
				params: { orgId, conversationId: id },
			});
		},
	});

	// A `?q=` entry sends exactly once per mount — the ref is the guard, since a
	// re-render (or the search param surviving in history) must not re-ask.
	const sentInitialQuestion = useRef(false);
	useEffect(() => {
		if (!initialQuestion || sentInitialQuestion.current) return;
		sentInitialQuestion.current = true;
		sendMessage(initialQuestion);
	}, [initialQuestion, sendMessage]);

	// Once the first message is sent, useChat holds the optimistic user message
	// and the streaming reply — render the thread immediately instead of leaving
	// the welcome state up until the server creates the conversation.
	const hasStarted = messages.length > 0;

	const selectedFarm = farms.find((f) => f.id === selectedFarmId);

	return (
		<div className="flex-1 flex flex-col h-full min-h-0">
			{hasStarted ? (
				<ChatMessages
					orgId={orgId}
					conversationId={conversationId}
					messages={messages}
					activeAgent={activeAgent}
					isStreaming={isStreaming}
					onRetry={retryLast}
				/>
			) : (
				/* Welcome state — scrolls when taller than the screen so the input stays visible */
				<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
					<div className="flex min-h-full flex-col items-center justify-center gap-6 p-8">
						{/* Icon */}
						<div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
							<Bot className="h-7 w-7 text-primary" />
						</div>

						{/* Heading */}
						<div className="text-center space-y-2 max-w-sm">
							<h2 className="text-xl font-semibold text-base-content">
								{t("grass.assistant.welcomeTitle", {
									defaultValue: "Como posso ajudar com a fazenda hoje?",
								})}
							</h2>
							<p className="text-sm text-base-content/50">
								{t("grass.assistant.welcomeSubtitle", {
									defaultValue:
										"Pergunte sobre pecuária, piquetes ou clima em todas as suas fazendas ou em uma fazenda específica.",
								})}
							</p>
						</div>

						{/* Farm scope selector */}
						{farms.length > 0 && (
							<div className="flex flex-wrap items-center justify-center gap-2 max-w-xl">
								<button
									type="button"
									onClick={() => setSelectedFarmId(undefined)}
									className={`flex items-center gap-1.5 px-3.5 py-2 min-h-9 rounded-full text-xs font-medium border transition-all ${
										selectedFarmId === undefined
											? "bg-primary text-primary-content border-primary shadow-sm"
											: "border-base-300 text-base-content/60 hover:border-primary/40 hover:text-base-content"
									}`}
								>
									{t("grass.assistant.allFarms", {
										defaultValue: "Todas as fazendas",
									})}
								</button>
								{farms.map((farm) => (
									<button
										key={farm.id}
										type="button"
										onClick={() => setSelectedFarmId(farm.id)}
										className={`flex items-center gap-1.5 px-3.5 py-2 min-h-9 rounded-full text-xs font-medium border transition-all ${
											selectedFarmId === farm.id
												? "bg-primary text-primary-content border-primary shadow-sm"
												: "border-base-300 text-base-content/60 hover:border-primary/40 hover:text-base-content"
										}`}
									>
										<MapPin className="h-3 w-3 shrink-0" />
										{farm.name}
									</button>
								))}
							</div>
						)}

						{/* Suggested prompts */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
							{suggestedPrompts.map((prompt) => (
								<button
									key={prompt}
									type="button"
									onClick={() => sendMessage(prompt)}
									disabled={isStreaming}
									className="text-left px-4 py-3 rounded-xl border border-base-300 bg-base-100 hover:border-primary/40 hover:bg-primary/5 text-sm text-base-content/70 hover:text-base-content transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed leading-snug"
								>
									{prompt}
								</button>
							))}
						</div>

						{/* Selected scope reminder */}
						{selectedFarm && (
							<p className="text-xs text-base-content/40 flex items-center gap-1">
								<MapPin className="h-3 w-3" />
								{t("grass.assistant.scopedTo", {
									defaultValue: "Conversa será focada em",
								})}{" "}
								<span className="font-medium text-base-content/60">
									{selectedFarm.name}
								</span>
							</p>
						)}
					</div>
				</div>
			)}

			{/* A turn that fails before `done` never navigates, so this route is the
			    one that has to show the failure. */}
			{error && (
				<div className="px-4 pb-1">
					<div className="text-error text-xs">{error}</div>
				</div>
			)}

			{/* Input at bottom */}
			<div className="border-t border-base-300 bg-base-100">
				<div className="max-w-3xl mx-auto w-full">
					<ChatInput
						orgId={orgId}
						onSend={sendMessage}
						disabled={isStreaming}
						autoStartVoice={startVoice}
					/>
				</div>
			</div>
		</div>
	);
}
